import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AppBar,
  Alert,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  InputBase,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LaptopIcon from '@mui/icons-material/Laptop';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import TranslateIcon from '@mui/icons-material/Translate';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { appBarEnvironmentColor } from '@/config/appEnvironment';
import { useColorMode } from '@/theme/ColorModeContext';
import { ModuleNavigation } from '@/modules/ModuleNavigation';
import { useRoles } from '@/auth/useRoles';
import { getApiErrorMessage } from '@/api/client';
import { useMarkCriticalInOrder, useMarkNotificationSeen, useNotifications } from '@/hooks/useNotifications';
import { getNotificationMessage, getNotificationTitle } from '@/components/notifications/notificationText';
import { useGlobalSearchResults } from '@/hooks/useGlobalSearchResults';
import type { GlobalSearchPage } from '@/api/globalSearch';
import { formatBelgianDate } from '@/utils/date';
import { setAdminPreview, useAdminPreview } from '@/auth/adminPreview';

const EXPANDED_DRAWER_WIDTH = 272;
const COLLAPSED_DRAWER_WIDTH = 76;
const SIDEBAR_COLLAPSED_KEY = 'allphi.navigation.sidebarCollapsed.v1';
const APP_HEADER_HEIGHT = 76;

/** Reads the persisted sidebar collapsed/expanded preference from localStorage. */
function getInitialSidebarCollapsed() {
  return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
}

/**
 * Top-level application shell: header bar (global search, notifications, account menu,
 * language/theme switches) and the collapsible side navigation drawer around page `children`.
 *
 * @param children - Page content rendered in the main content area.
 * @param searchTerm - Current value of the global search input (controlled by the parent).
 * @param onSearch - Callback invoked as the user types in or clears the global search field.
 * @returns The rendered application layout.
 */
export function AppLayout({
  children,
  searchTerm,
  onSearch,
}: {
  children: ReactNode;
  searchTerm: string;
  onSearch?: (term: string) => void;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [brandLogoUnavailable, setBrandLogoUnavailable] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { mode, toggleColorMode } = useColorMode();
  const { instance, accounts } = useMsal();
  const { canWrite } = useRoles();
  const adminPreview = useAdminPreview();
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const compactNavigation = isDesktop && sidebarCollapsed;
  const drawerWidth = compactNavigation ? COLLAPSED_DRAWER_WIDTH : EXPANDED_DRAWER_WIDTH;
  const {
    data: globalSearchResults,
    isFetching: globalSearchLoading,
    isError: globalSearchError,
  } = useGlobalSearchResults(debouncedSearchTerm, searchExpanded);

  // Debounce the global search query so results aren't fetched on every keystroke.
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => window.clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (searchExpanded) {
      window.setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [searchExpanded]);

  const notificationsOpen = Boolean(notificationsAnchorEl);
  const { data: unreadNewNotifications } = useNotifications(
    { limit: 200, status: 'New', onlyUnread: true },
    true,
  );
  const { data: notifications } = useNotifications(
    { limit: 20, status: 'New', onlyUnread: true },
    notificationsOpen,
  );
  const markSeen = useMarkNotificationSeen();
  const markInOrder = useMarkCriticalInOrder();

  const account = accounts[0];
  const initials =
    account?.name
      ?.split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('') ?? '?';

  /** Toggles the sidebar between expanded and compact modes, persisting the choice for next visit. */
  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const drawer = (
    <Box
      role="navigation"
      aria-label="Main navigation"
      sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}
    >
      <Toolbar
        sx={{
          minHeight: `${APP_HEADER_HEIGHT}px !important`,
          px: compactNavigation ? '8px !important' : '20px !important',
          gap: compactNavigation ? 0.5 : 1.5,
          justifyContent: 'flex-start',
          backgroundColor: appBarEnvironmentColor(theme.palette.mode),
        }}
      >
        <Tooltip title={t('modules.home')}>
          <ButtonBase
            onClick={() => {
              navigate('/');
              setMobileOpen(false);
            }}
            aria-label={t('modules.home')}
            sx={(currentTheme) => ({
              display: 'grid',
              placeItems: 'center',
              width: compactNavigation ? 36 : 42,
              height: compactNavigation ? 36 : 42,
              flexShrink: 0,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: alpha(currentTheme.palette.primary.main, 0.18),
              borderRadius: 1.5,
              backgroundColor: alpha(currentTheme.palette.primary.main, 0.07),
              '&:hover': { backgroundColor: alpha(currentTheme.palette.primary.main, 0.13) },
            })}
          >
            {brandLogoUnavailable ? (
              <LaptopIcon color="primary" sx={{ fontSize: 25 }} />
            ) : (
              <Box
                component="img"
                src="/brand/allphi-logo.png"
                alt=""
                onError={() => setBrandLogoUnavailable(true)}
                sx={{ display: 'block', width: 30, height: 30, objectFit: 'contain' }}
              />
            )}
          </ButtonBase>
        </Tooltip>
        <Box sx={{ minWidth: 0, display: compactNavigation ? 'none' : 'block' }}>
          <Typography variant="subtitle1" fontWeight={750} lineHeight={1.2} noWrap>
            {t('app.title')}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25, letterSpacing: '0.02em' }}
            noWrap
          >
            {t('app.platformSubtitle')}
          </Typography>
        </Box>
        {isDesktop && (
          <Tooltip title={t(compactNavigation ? 'app.expandSidebar' : 'app.collapseSidebar')}>
            <IconButton
              size="small"
              onClick={toggleSidebar}
              aria-label={t(compactNavigation ? 'app.expandSidebar' : 'app.collapseSidebar')}
              sx={{
                ml: compactNavigation ? 0 : 'auto',
                p: compactNavigation ? 0 : 0.75,
                width: compactNavigation ? 20 : 34,
                height: compactNavigation ? 28 : 34,
                flexShrink: 0,
                color: 'text.secondary',
                '& .MuiSvgIcon-root': { fontSize: compactNavigation ? 19 : 22 },
                '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              {compactNavigation ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <ModuleNavigation
          pathname={location.pathname}
          compact={compactNavigation}
          onNavigate={(path) => {
            navigate(path);
            setMobileOpen(false);
          }}
        />
      </Box>
    </Box>
  );

  /** Marks a notification as seen and navigates to its related entity, focusing that row. */
  const handleNotificationClick = (path: string, notificationEventId: number, entityId: number) => {
    markSeen.mutate(notificationEventId);
    const focusKey = path === '/assets' ? 'focusAssetId' : 'focusContractId';
    navigate(`${path}?${focusKey}=${entityId}`);
    setNotificationsAnchorEl(null);
  };

  /** Marks a critical item as resolved ("in order") and reports success/failure via a snackbar. */
  const handleMarkInOrder = async (criticalItemId: number) => {
    try {
      await markInOrder.mutateAsync({ criticalItemId });
      enqueueSnackbar(t('app.markedInOrderSuccess'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error), { variant: 'error' });
    }
  };

  /** Translates a global search result's page category into its display label. */
  const globalSearchPageLabel = (page: GlobalSearchPage) => {
    switch (page) {
      case 'assets':
        return t('app.searchPages.assets');
      case 'contracts':
        return t('app.searchPages.contracts');
      case 'documents':
        return t('app.searchPages.documents');
      case 'notifications':
        return t('app.searchPages.notifications');
      case 'mobility':
        return t('app.searchPages.mobility');
      default:
        return page;
    }
  };

  /** Navigates to a chosen global search result and collapses the search box. */
  const openGlobalSearchResult = (path: string) => {
    navigate(path);
    setSearchExpanded(false);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={(currentTheme) => ({
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          transition: currentTheme.transitions.create(['width', 'margin-left']),
        })}
      >
        <Toolbar sx={{ minHeight: `${APP_HEADER_HEIGHT}px !important` }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { md: 'none' } }}
            aria-label={t('app.openNavigation')}
          >
            <MenuIcon />
          </IconButton>

          <ClickAwayListener onClickAway={() => setSearchExpanded(false)}>
            <Box
              sx={{
                position: 'relative',
                mr: 2,
                flexGrow: searchExpanded ? 1 : 0,
                width: searchExpanded ? { xs: '100%', sm: 480 } : 40,
                maxWidth: 480,
                transition: theme.transitions.create(['width', 'flex-grow']),
              }}
            >
              {!searchExpanded ? (
                <Tooltip title={t('app.openGlobalSearch')}>
                  <IconButton
                    color="inherit"
                    onClick={() => setSearchExpanded(true)}
                    aria-label={t('app.openGlobalSearch')}
                  >
                    <Badge color="primary" variant="dot" invisible={!searchTerm.trim()}>
                      <SearchIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: 1.5,
                      backgroundColor: alpha(theme.palette.text.primary, 0.06),
                      '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.1) },
                    }}
                  >
                    <SearchIcon fontSize="small" sx={{ ml: 1.5, flexShrink: 0 }} />
                    <InputBase
                      inputRef={searchInputRef}
                      value={searchTerm}
                      placeholder={t('app.searchPlaceholder')}
                      inputProps={{ 'aria-label': t('app.openGlobalSearch') }}
                      onChange={(event) => onSearch?.(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                          setSearchExpanded(false);
                        }
                        if (event.key === 'Enter' && globalSearchResults?.[0]) {
                          openGlobalSearchResult(globalSearchResults[0].path);
                        }
                      }}
                      sx={{ color: 'inherit', px: 1.25, py: 1, width: '100%' }}
                    />
                    {globalSearchLoading ? (
                      <CircularProgress size={18} sx={{ mr: 1.25 }} />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => {
                          onSearch?.('');
                          setSearchExpanded(false);
                        }}
                        aria-label={t('app.clearSearch')}
                        sx={{ mr: 0.75 }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      left: 0,
                      width: '100%',
                      minWidth: { xs: 300, sm: 480 },
                      maxHeight: 480,
                      overflowY: 'auto',
                      zIndex: theme.zIndex.modal + 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    {searchTerm.trim().length < 2 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {t('app.searchMinimum')}
                      </Typography>
                    ) : globalSearchError ? (
                      <Typography variant="body2" color="error" sx={{ p: 2 }}>
                        {t('app.searchError')}
                      </Typography>
                    ) : !globalSearchLoading && (globalSearchResults?.length ?? 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                        {t('app.searchNoResults', { term: searchTerm })}
                      </Typography>
                    ) : (
                      globalSearchResults?.map((result) => (
                        <ButtonBase
                          key={result.key}
                          onClick={() => openGlobalSearchResult(result.path)}
                          sx={{
                            display: 'flex',
                            width: '100%',
                            textAlign: 'left',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-of-type': { borderBottom: 0 },
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={700} noWrap>{result.title}</Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {result.description}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            variant="outlined"
                            color="primary"
                            label={globalSearchPageLabel(result.page)}
                            sx={{ flexShrink: 0 }}
                          />
                        </ButtonBase>
                      ))
                    )}
                  </Paper>
                </>
              )}
            </Box>
          </ClickAwayListener>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={mode === 'light' ? t('app.darkMode') : t('app.lightMode')}>
            <IconButton color="inherit" onClick={toggleColorMode} aria-label={mode === 'light' ? t('app.darkMode') : t('app.lightMode')}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t('app.language')}>
            <IconButton color="inherit" onClick={(e) => setLanguageAnchorEl(e.currentTarget)} aria-label={t('app.language')}>
              <TranslateIcon />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={languageAnchorEl} open={Boolean(languageAnchorEl)} onClose={() => setLanguageAnchorEl(null)}>
            <MenuItem
              selected={i18n.language.startsWith('nl')}
              onClick={() => {
                void i18n.changeLanguage('nl');
                setLanguageAnchorEl(null);
              }}
            >
              {t('app.dutch')}
            </MenuItem>
            <MenuItem
              selected={i18n.language.startsWith('en')}
              onClick={() => {
                void i18n.changeLanguage('en');
                setLanguageAnchorEl(null);
              }}
            >
              {t('app.english')}
            </MenuItem>
          </Menu>

          <Tooltip title={t('app.notifications')}>
            <IconButton
              color="inherit"
              onClick={(e) => setNotificationsAnchorEl(e.currentTarget)}
              aria-label={t('app.openNotifications')}
            >
              <Badge badgeContent={unreadNewNotifications?.length ?? 0} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={account?.username ?? t('app.accountMenu')}>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }} aria-label={t('app.accountMenu')}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{initials}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>{account?.name ?? account?.username}</MenuItem>
            <Divider />
            <MenuItem onClick={() => {
              setAdminPreview(false);
              void instance.logoutRedirect();
            }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              {t('app.signOut')}
            </MenuItem>
          </Menu>

          <Popover
            open={notificationsOpen}
            anchorEl={notificationsAnchorEl}
            onClose={() => setNotificationsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 460,
                maxWidth: 'calc(100vw - 16px)',
                maxHeight: 560,
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 14px 40px rgba(0,0,0,0.18)',
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.primary.main, 0.03)})`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box>
                  <Typography variant="h6" sx={{ fontSize: '1.55rem', lineHeight: 1.15, fontWeight: 800 }}>
                    {t('app.notificationCenter')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('app.notificationSubtitle')}
                  </Typography>
                </Box>
                <Chip
                  label={t('app.unreadNewCount', { count: notifications?.length ?? 0 })}
                  color="error"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Box>
            {notifications && notifications.length > 0 ? (
              <Box sx={{ p: 1.25, overflowY: 'auto', maxHeight: 470 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="text"
                  onClick={() => {
                    navigate('/notifications');
                    setNotificationsAnchorEl(null);
                  }}
                  sx={{ mb: 1 }}
                >
                  {t('app.openNotificationCenterPage')}
                </Button>
                {notifications.map((notification) => {
                  const destination = notification.entityType === 'Asset'
                    ? '/assets'
                    : notification.entityType === 'ExpenseClaim'
                      ? '/mobility/claims'
                      : '/contracts/all';
                  const entityLabel = notification.entityType === 'Asset'
                    ? t('app.entityAsset')
                    : notification.entityType === 'ExpenseClaim'
                      ? t('app.entityExpenseClaim')
                      : t('app.entityContract');
                  return (
                    <Box
                      key={notification.notificationEventId}
                      sx={{
                        mb: 1,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: notification.seen ? 'divider' : alpha(theme.palette.error.main, 0.32),
                        backgroundColor: notification.seen
                          ? theme.palette.background.paper
                          : alpha(theme.palette.error.main, 0.045),
                      }}
                    >
                      <Stack spacing={1.1}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3, pr: 1 }}>
                            {getNotificationTitle(notification, t)}
                          </Typography>
                          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                            <Chip
                              size="small"
                              label={entityLabel}
                              color={notification.entityType === 'Asset' ? 'info' : notification.entityType === 'ExpenseClaim' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                            {!notification.seen && (
                              <Chip size="small" label={t('app.newBadge')} color="error" />
                            )}
                          </Stack>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                          {getNotificationMessage(notification, t)}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="caption" color="text.secondary">
                            {t('app.dueLabel', { date: formatBelgianDate(notification.dueDate) })}
                          </Typography>
                          <Typography variant="caption" color="text.disabled">•</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('app.reminderLabel', { date: formatBelgianDate(notification.reminderDate) })}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleNotificationClick(destination, notification.notificationEventId, notification.entityId)}
                          >
                            {t('app.openButton')}
                          </Button>
                          {canWrite && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleMarkInOrder(notification.criticalItemId)}
                            >
                              {t('app.inOrder')}
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 34, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {t('app.allClear')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('app.noActiveNotifications')}
                </Typography>
              </Box>
            )}
          </Popover>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={(currentTheme) => ({
          width: { md: drawerWidth },
          flexShrink: { md: 0 },
          transition: currentTheme.transitions.create('width'),
        })}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isDesktop ? drawerWidth : EXPANDED_DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width'),
              backgroundImage: (currentTheme) =>
                `linear-gradient(180deg, ${alpha(currentTheme.palette.primary.main, 0.035)} 0, transparent 180px)`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={(currentTheme) => ({
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          transition: currentTheme.transitions.create('width'),
        })}
      >
        <Toolbar sx={{ minHeight: `${APP_HEADER_HEIGHT}px !important` }} />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {adminPreview && (
            <Alert
              severity="info"
              variant="outlined"
              action={(
                <Button color="inherit" size="small" onClick={() => setAdminPreview(false)}>
                  {t('admin.endPreview')}
                </Button>
              )}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight={650}>{t('admin.previewActive')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('admin.previewExplanation')}</Typography>
            </Alert>
          )}
          {children}
        </Box>
      </Box>
    </Box>
  );
}
